import { useEffect, useRef } from 'react';
import './Technology.css';

/* ── Stats bar data ─────────────────────────────────────── */
const STATS = [
    { value: 'UE5', label: 'Engine' },
    { value: '3', label: 'Drone Models' },
    { value: '7', label: 'Crop Varieties' },
    { value: '3', label: 'Growth Stages' },
    { value: 'USB', label: 'Taranis Connect' },
];

/* ── Built pillars ──────────────────────────────────────── */
const PILLARS = [
    {
        id: '01',
        tag: 'Rendering Engine',
        heading: 'Unreal Engine 5',
        body: 'The simulator is built on Unreal Engine 5, enabling high-performance real-time rendering and precise physics simulation across all environments and drone models.',
    },
    {
        id: '02',
        tag: 'Flight Logic',
        heading: 'Physics-Driven Flight Model',
        body: 'Every movement — lift, tilt, acceleration, and control response — is governed entirely by physics logic, not pre-defined animations. The drone behaves as a real aircraft would under the same inputs.',
    },
    {
        id: '03',
        tag: 'Pilot Validation',
        heading: 'Built From Real Flight Hours',
        body: 'The flight model is designed and continuously refined using insights from professional agri drone pilots with extensive real-world operational experience. What you fly in Agri Drone Simulator reflects what you will face in the field.',
    },
    {
        id: '04',
        tag: 'Hardware Integration',
        heading: 'FrSky Taranis Q X7',
        body: 'The simulator integrates the FrSky Taranis Q X7 transmitter via USB. Middleware maps transmitter inputs into real-time control signals inside the simulation — the same physical controls used to fly a real agricultural drone.',
    },
];

/* ── How It Works steps ─────────────────────────────────── */
const STEPS = [
    {
        num: '01',
        head: 'Select Your Setup',
        body: 'Choose a drone type — spreader or sprayer — and select a crop environment. Each crop offers multiple growth stages: seeding, pre-harvest, and harvest.',
    },
    {
        num: '02',
        head: 'Connect & Configure',
        body: 'Plug in the Taranis Q X7 transmitter via USB. The system maps your transmitter inputs in real time, ready for flight within seconds.',
    },
    {
        num: '03',
        head: 'Fly With Physics',
        body: 'Take control. The drone responds to every input through physics-based calculations — the same principles governing a real agricultural drone in the field.',
    },
    {
        num: '04',
        head: 'Switch & Practice',
        body: 'Mid-session, switch between drone models, crop types, and growth stages. Repeat operations — spraying, navigation, field coverage — until the workflow is second nature.',
    },
];

/* ── Scroll reveal hook ─────────────────────────────────── */
function useReveal() {
    const refs = useRef([]);
    useEffect(() => {
        const io = new IntersectionObserver(
            entries => entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('st-visible');
                    io.unobserve(e.target);
                }
            }),
            { threshold: 0.12 }
        );
        refs.current.forEach(el => el && io.observe(el));
        return () => io.disconnect();
    }, []);
    return refs;
}

export default function Technology() {
    const refs = useReveal();

    return (
        <section className="st-wrap" id="technology">
            <div className="st-bg-grid" aria-hidden="true" />

            <div className="st-inner">

                {/* ── Section label ── */}
                <div
                    className="st-eyebrow-row st-reveal"
                    ref={el => refs.current[90] = el}
                >
                    <span className="st-eyebrow-dot" aria-hidden="true" />
                    <span className="st-eyebrow-text">The Technology Behind the Simulation</span>
                </div>

                {/* ── Stats bar ── */}
                <div
                    className="st-stats st-reveal"
                    ref={el => refs.current[91] = el}
                >
                    {STATS.map(s => (
                        <div key={s.label} className="st-stat">
                            <span className="st-stat-value">{s.value}</span>
                            <span className="st-stat-label">{s.label}</span>
                        </div>
                    ))}
                </div>

                {/* ══════════════════════
            HOW IT'S BUILT
        ══════════════════════ */}
                {/* <div
                    className="st-block-header st-reveal"
                    ref={el => refs.current[92] = el}
                >
                    <h2 className="st-h2">How It's<em>Built</em></h2>
                    <p className="st-block-sub">
                        Four interconnected systems — engine, physics, hardware, and pilot expertise —
                        combine to create a simulation that closely mirrors real agri drone operations.
                    </p>
                </div> */}

                {/* Pillar cards */}
                {/* <div className="st-pillars">
                    {PILLARS.map((p, i) => (
                        <article
                            key={p.id}
                            className="st-pillar st-reveal"
                            ref={el => refs.current[i] = el}
                        >
                            <div className="st-pillar-top">
                                <span className="st-pillar-tag">{p.tag}</span>
                                <span className="st-pillar-id" aria-hidden="true">{p.id}</span>
                            </div>
                            <h3 className="st-pillar-h3">{p.heading}</h3>
                            <p className="st-pillar-body">{p.body}</p>
                            <div className="st-pillar-line" aria-hidden="true" />
                            <div className="st-pillar-glow" aria-hidden="true" />
                        </article>
                    ))}
                </div> */}

                {/* ══════════════════════
            HOW IT WORKS
        ══════════════════════ */}
                <div
                    className="st-block-header st-reveal"
                    ref={el => refs.current[93] = el}
                    style={{ marginTop: '100px' }}
                >
                    <h2 className="st-h2">How It<em>Works</em></h2>
                    <p className="st-block-sub">
                        A structured, repeatable workflow designed to build genuine operational
                        confidence before a pilot sets foot near a real drone.
                    </p>
                </div>

                {/* Steps */}
                <div className="st-steps">
                    {STEPS.map((s, i) => (
                        <div
                            key={s.num}
                            className="st-step st-reveal"
                            ref={el => refs.current[10 + i] = el}
                        >
                            <div className="st-step-num">{s.num}</div>
                            <div className="st-step-connector" aria-hidden="true" />
                            <div className="st-step-content">
                                <h4 className="st-step-head">{s.head}</h4>
                                <p className="st-step-body">{s.body}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Closing statement ── */}
                <div
                    className="st-close st-reveal"
                    ref={el => refs.current[94] = el}
                >
                    <div className="st-close-rule" aria-hidden="true" />
                    <p className="st-close-text">
                        This combination of engine technology, physics simulation, real hardware
                        integration, and pilot validation creates a system that gives beginners
                        a safe, accurate, and repeatable environment to build real-world readiness.
                    </p>
                </div>

            </div>
        </section>
    );
}